import { DateTime } from "luxon";

const TimeBreakpoint = {
	year: 31536000000,
	month: 2592000000,
	week: 604800000,
	day: 86400000,
	hour: 3600000,
	minute: 60000,
	second: 1000,
	now: 5000,
};

/**
 *
 * @param {String | Object | Date} date parseable string/object/date for new Date()
 * @param {String} granular string with characters for time breakpoints.
 * Characters:
 *   n: now
 *   s: seconds
 *   m: minutes
 *   h: hours
 *   d: days
 *   w: weeks
 *   M: months
 *   y: years
 */
const timeAgo = (date, granular = "dMy") => {
	date = parseDate(date);
	const diff = Date.now() - date;
	let timeAgo = null;

	if (granular.includes("y") && diff >= TimeBreakpoint.year) {
		const time = Math.floor(diff / TimeBreakpoint.year);
		timeAgo = `${time} year${plural(time)} ago`;
	} else if (granular.includes("M") && diff >= TimeBreakpoint.month) {
		const time = Math.floor(diff / TimeBreakpoint.month);
		timeAgo = `${time} month${plural(time)} ago`;
	} else if (granular.includes("w") && diff >= TimeBreakpoint.week) {
		const time = Math.floor(diff / TimeBreakpoint.week);
		timeAgo = `${time} week${plural(time)} ago`;
	} else if (granular.includes("d") && diff >= TimeBreakpoint.day) {
		const time = Math.floor(diff / TimeBreakpoint.day);
		timeAgo = `${time} day${plural(time)} ago`;
	} else if (granular.includes("h") && diff >= TimeBreakpoint.hour) {
		const time = Math.floor(diff / TimeBreakpoint.hour);
		timeAgo = `${time} hour${plural(time)} ago`;
	} else if (granular.includes("m") && diff >= TimeBreakpoint.minute) {
		const time = Math.floor(diff / TimeBreakpoint.minute);
		timeAgo = `${time} minute${plural(time)} ago`;
	} else if (granular.includes("n") && diff < TimeBreakpoint.now) {
		timeAgo = "now";
	} else if (granular.includes("s") && diff >= TimeBreakpoint.second) {
		const time = Math.floor(diff / TimeBreakpoint.second);
		timeAgo = `${time} second${plural(time)} ago`;
	}

	return timeAgo;
};

const plural = number => (number === 1 ? "" : "s");

const isToday = date => {
	date = parseDate(date);
	const diff = new Date().setHours(0, 0, 0, 0) - date;
	return !!date.valueOf() && diff <= 0;
};

const hideTodayDate = date => {
	date = parseDate(date);
	if (isNaN(date.valueOf())) return "--";
	return isToday(date) ? date.toLocaleTimeString() : date.toDateString();
};

const alarmDate = date => {
	const time = timeAgo(date, "hmns");
	return time && time.includes("hour") ? hideTodayDate(date) : time;
};

const fullDateTime = date => {
	date = parseDate(date);
	if (isNaN(date.valueOf())) return "--";
	return `${date.toDateString()}, ${date.toLocaleTimeString()}`;
};

const parseDate = date => {
	let numDate = Number(date);
	if (isNaN(numDate)) return new Date(date);
	return new Date(numDate);
};

const formatShortDateTime = date => {
	date = parseDate(date);
	const options = { dateStyle: "short", timeStyle: "short" };
	return Intl.DateTimeFormat("en-US", options).format(date);
};

const changeTimeZone = (date, timeZone) => {
	return new Date(
		new Date(date).toLocaleString("en-US", { timeZone })
	).toJSON();
};

const isoTz = date => {
	return DateTime.fromISO(date);
};

const fromNow = duration => {
	return DateTime.now()
		.minus(duration)
		.startOf(duration.hours ? "second" : "day")
		.toISO();
};

const endOf = (date, unit = "day") => {
	return DateTime.fromISO(date)
		.endOf(unit)
		.toISO();
};

export {
	parseDate,
	fromNow,
	isToday,
	hideTodayDate,
	fullDateTime,
	formatShortDateTime,
	changeTimeZone,
	isoTz,
	timeAgo,
	alarmDate,
	plural,
	endOf,
};
